import {
  Incognito,
  LogoAwsColored,
  LogoAzureColored,
  LogoBackstageColored,
  LogoBitbucketColored,
  LogoDatadogColored,
  LogoGcpColored,
  LogoGithub,
  LogoGitlabColored,
  LogoGraphqlColored,
  LogoMsAzureColored,
  LogoMsTeamsColored,
  LogoServicenowColored,
  LogoSlackColored,
  LogoSpaceliftColored,
  LogoTerraformColored,
  Webhook as WebhookIcon,
} from "components/icons/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import { IntegrationView } from "../types";
import AiIntegrationsList from "./AI";
import AwsIntegrationsList from "./AWS";
import AwsIntegrationDetailsPage from "./AWS/detailsPage";
import { default as AzureIntegrationsList } from "./Azure";
import AzureIntegrationDetailsPage from "./Azure/detailsPage";
import AzureDevOps from "./AzureDevOps";
import AzureDevOpsDetailsPage from "./AzureDevOps/detailsPage";
import BackstageIntegrationsList from "./Backstage";
import BitbucketCloud from "./BitbucketCloud";
import BitbucketCloudDetailsPage from "./BitbucketCloud/detailsPage";
import BitbucketDataCenter from "./BitbucketDataCenter";
import BitbucketDataCenterDetailsPage from "./BitbucketDataCenter/detailsPage";
import { IntegrationId } from "./constants";
import Github from "./Github";
import GithubDetailsPage from "./Github/detailsPage";
import Gitlab from "./Gitlab";
import GitlabDetailsPage from "./Gitlab/detailsPage";
import ServiceNowIntegrationsList from "./ServiceNow";
import ServiceNowIntegrationDetailsPage from "./ServiceNow/ItemPage";
import SlackIntegration from "./Slack";
import VCSAgentPools from "./VCSAgentPools";
import VCSAgentPool from "./VCSAgentPools/ItemPage";
import Webhooks from "./Webhooks";
import Webhook from "./Webhooks/ItemPage";

export const integrationIds = Object.values(IntegrationId).sort((a, b) => a.localeCompare(b));
export const isValidIntegrationId = (id: string | undefined): id is IntegrationId => {
  return integrationIds.includes(id as IntegrationId);
};

export const getIntegrationById = (
  id: IntegrationId,
  flags: Record<string, boolean> = {}
): IntegrationView | undefined => {
  return getIntegrationRegistry(flags)[id];
};

const getIntegrationRegistry = (
  flags: Record<string, boolean> = {}
): Partial<Record<IntegrationId, IntegrationView>> => {
  return {
    ...defaultIntegrationRegistry,
    ...(flags.intentNewLayout && { [IntegrationId.AI]: aiIntegration }),
  };
};

const aiIntegration: IntegrationView = {
  id: IntegrationId.AI,
  name: "AI",
  description: "Use AI to manage your infrastructure using Intent.",
  icon: LogoSpaceliftColored,
  docsLink: getDocsUrl("/integrations/ai"),
  hasViewScreen: true,
  type: "ai",
  page: AiIntegrationsList,
  hasSearch: true,
};

/**
 * Integration Registry
 *
 * When adding a new integration:
 * - Use `newUntil: "YYYY-MM-DD"` to show a "New" badge that automatically expires
 * - The badge will be visible until the END of the specified date (23:59:59 UTC)
 * - Example: `newUntil: "2026-02-26"` shows badge until Feb 26, 2026
 * - Leave `newUntil` undefined for existing integrations (no badge)
 */
const defaultIntegrationRegistry: Record<Exclude<IntegrationId, IntegrationId.AI>, IntegrationView> = {
  [IntegrationId.AWS]: {
    id: IntegrationId.AWS,
    name: "AWS",
    description: "Use IAM Role Assumption to securely access AWS from Spacelift runs.",
    icon: LogoAwsColored,
    docsLink: getDocsUrl("/integrations/cloud-providers/aws"),
    hasViewScreen: true,
    page: AwsIntegrationsList,
    detailsPage: AwsIntegrationDetailsPage,
    hasSearch: true,
    type: "cloud",
  },
  [IntegrationId.AZURE]: {
    id: IntegrationId.AZURE,
    name: "Azure",
    description: "Grant Spacelift temporary access to Azure via identity tokens.",
    icon: LogoMsAzureColored,
    docsLink: getDocsUrl("/integrations/cloud-providers/azure"),
    hasViewScreen: true,
    page: AzureIntegrationsList,
    detailsPage: AzureIntegrationDetailsPage,
    hasSearch: true,
    type: "cloud",
  },
  [IntegrationId.AZURE_DEVOPS]: {
    id: IntegrationId.AZURE_DEVOPS,
    name: "Azure DevOps",
    description: "Use Azure Repos to drive Spacelift runs and workflows.",
    icon: LogoAzureColored,
    docsLink: getDocsUrl("/integrations/source-control/azure-devops"),
    type: "source_code",
    page: AzureDevOps,
    detailsPage: AzureDevOpsDetailsPage,
    hasViewScreen: true,
    hasSearch: true,
  },
  [IntegrationId.BACKSTAGE]: {
    id: IntegrationId.BACKSTAGE,
    name: "Backstage",
    description: "Bring Spacelift's stacks and runs to your developer portal.",
    icon: LogoBackstageColored,
    docsLink: getDocsUrl("/integrations/external-integrations/backstage"),
    hasViewScreen: true,
    type: "idp",
    page: BackstageIntegrationsList,
    hasSearch: false,
  },
  [IntegrationId.BITBUCKET_CLOUD]: {
    id: IntegrationId.BITBUCKET_CLOUD,
    name: "Bitbucket Cloud",
    description: "Start runs from commits or PRs in Bitbucket Cloud.",
    icon: LogoBitbucketColored,
    docsLink: getDocsUrl("/integrations/source-control/bitbucket-cloud"),
    type: "source_code",
    page: BitbucketCloud,
    detailsPage: BitbucketCloudDetailsPage,
    hasViewScreen: true,
    hasSearch: true,
  },
  [IntegrationId.BITBUCKET_DATA_CENTER]: {
    id: IntegrationId.BITBUCKET_DATA_CENTER,
    name: "Bitbucket Data Center",
    description: "Secure Bitbucket DC integration via VCS agent pools.",
    icon: LogoBitbucketColored,
    docsLink: getDocsUrl("/integrations/source-control/bitbucket-datacenter-server"),
    type: "source_code",
    page: BitbucketDataCenter,
    detailsPage: BitbucketDataCenterDetailsPage,
    hasViewScreen: true,
    hasSearch: true,
  },
  [IntegrationId.DATADOG]: {
    id: IntegrationId.DATADOG,
    name: "Datadog",
    description: "Monitor your applications with Datadog.",
    icon: LogoDatadogColored,
    docsLink: getDocsUrl("/integrations/observability/datadog"),
    type: "observability",
  },
  [IntegrationId.GCP]: {
    id: IntegrationId.GCP,
    name: "Google Cloud Platform",
    description: "Authenticate Spacelift to GCP with federated identity.",
    icon: LogoGcpColored,
    docsLink: getDocsUrl("/integrations/cloud-providers/oidc/gcp-oidc"),
    type: "cloud",
  },
  [IntegrationId.GITHUB]: {
    id: IntegrationId.GITHUB,
    name: "GitHub",
    description: "Trigger runs from PRs and pushes. Supports GitHub Cloud and Enterprise",
    icon: LogoGithub,
    docsLink: getDocsUrl("/integrations/source-control/github"),
    type: "source_code",
    page: Github,
    detailsPage: GithubDetailsPage,
    hasViewScreen: true,
    hasSearch: true,
  },
  [IntegrationId.GITLAB]: {
    id: IntegrationId.GITLAB,
    name: "GitLab",
    description: "Connect GitLab Cloud or self-hosted to sync infra with code changes.",
    icon: LogoGitlabColored,
    docsLink: getDocsUrl("/integrations/source-control/gitlab"),
    type: "source_code",
    page: Gitlab,
    detailsPage: GitlabDetailsPage,
    hasViewScreen: true,
    hasSearch: true,
  },
  [IntegrationId.GRAPHQL_API]: {
    id: IntegrationId.GRAPHQL_API,
    name: "GraphQL API",
    description: "Automate, extend, and integrate via  a robust GraphQL interface.",
    icon: LogoGraphqlColored,
    docsLink: getDocsUrl("/integrations/api"),
    type: "api",
  },
  [IntegrationId.SERVICENOW]: {
    id: IntegrationId.SERVICENOW,
    name: "ServiceNow",
    description: "Tie Spacelift's activity to ITSM workflows in ServiceNow.",
    icon: LogoServicenowColored,
    docsLink: getDocsUrl("/integrations/external-integrations/servicenow"),
    hasViewScreen: true,
    type: "idp",
    page: ServiceNowIntegrationsList,
    detailsPage: ServiceNowIntegrationDetailsPage,
    hasSearch: false,
  },
  [IntegrationId.SLACK]: {
    id: IntegrationId.SLACK,
    name: "Slack",
    description: "Send stack alerts and policy decisions to Slack channels.",
    icon: LogoSlackColored,
    docsLink: getDocsUrl("/integrations/chatops/slack"),
    hasViewScreen: true,
    type: "communication",
    page: SlackIntegration,
    hasSearch: false,
  },
  [IntegrationId.SPACECTL]: {
    id: IntegrationId.SPACECTL,
    name: "(spacectl) CLI",
    description: "Interact with Spacelift programmatically via your terminal or CI pipeline.",
    icon: LogoSpaceliftColored,
    docsLink: getDocsUrl("/concepts/spacectl"),
    type: "api",
  },
  [IntegrationId.TEAMS]: {
    id: IntegrationId.TEAMS,
    name: "Microsoft Teams",
    description: "Notify Teams channels on infrastructure changes or approvals.",
    icon: LogoMsTeamsColored,
    docsLink: getDocsUrl("/integrations/chatops/msteams"),
    type: "communication",
  },
  [IntegrationId.TERRAFORM_PROVIDER]: {
    id: IntegrationId.TERRAFORM_PROVIDER,
    name: "Terraform Provider",
    description: "Manage Spacelift itself as code: stacks, contexts, policies and more.",
    icon: LogoTerraformColored,
    docsLink: getDocsUrl("/vendors/terraform/terraform-provider"),
    type: "api",
  },
  [IntegrationId.VCS_AGENT_POOLS]: {
    id: IntegrationId.VCS_AGENT_POOLS,
    name: "VCS Agent Pools",
    description: "Run integrations behind your firewall, fully under your control.",
    icon: Incognito,
    type: "source_code",
    docsLink: getDocsUrl(
      /* @ignore-checking-sh-docs */
      "/concepts/vcs-agent-pools"
    ),
    hasViewScreen: true,
    page: VCSAgentPools,
    detailsPage: VCSAgentPool,
    hasSearch: true,
  },
  [IntegrationId.WEBHOOKS]: {
    id: IntegrationId.WEBHOOKS,
    name: "Webhooks",
    description: "Push structured event data to any external system.",
    icon: WebhookIcon,
    docsLink: getDocsUrl("/integrations/webhooks"),
    hasViewScreen: true,
    type: "observability",
    page: Webhooks,
    detailsPage: Webhook,
    hasSearch: true,
  },
};
